import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axios from "axios";
import { toast } from "react-toastify";
import apiClient, { v1Backend } from "services/api/client";
import { clearAuthToken } from "utils/authToken";
import { navigateToDashboard } from "utils/navigation";
import BrandWorldBankDetails from "./BrandWorldBankDetails";

const FONT =
  'Poppins, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_PATTERN = /^[0-9]{6,20}$/;
const EMPTY = {
  accountHolderName: "",
  bankAccountNo: "",
  confirmAccountNo: "",
  ifsc: "",
  bankName: "",
  branchName: "",
  accountType: "Current",
};

const C = {
  brand: "#2261A2",
  brandDark: "#17467A",
  ink: "#101828",
  body: "#344054",
  muted: "#475467",
  line: "#D0D5DD",
  soft: "#F5F8FC",
  error: "#D92D20",
  success: "#12B76A",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: FONT,
    fontSize: 14.5,
    fontWeight: 500,
    color: C.ink,
    borderRadius: "10px",
    bgcolor: "#fff",
    "& fieldset": { borderColor: C.line },
    "&:hover fieldset": { borderColor: "#98A2B3" },
    "&.Mui-focused fieldset": { borderColor: C.brand, borderWidth: "1.5px" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(34, 97, 162, 0.12)" },
    "&.Mui-error fieldset": { borderColor: C.error },
  },
  "& .MuiOutlinedInput-input": { py: "10px", px: "14px" },
  "& .MuiOutlinedInput-input::placeholder": { color: "#98A2B3", opacity: 1 },
  "& .MuiFormHelperText-root": {
    fontFamily: FONT,
    fontSize: 12,
    mx: 0.25,
    mt: 0.5,
  },
};

// Dropdown list for "Account type" — same type scale as the inputs.
const menuProps = {
  PaperProps: {
    sx: {
      mt: 0.75,
      borderRadius: "10px",
      border: "1px solid #EAECF0",
      boxShadow: "0 12px 24px -6px rgba(16, 24, 40, 0.18)",
      "& .MuiList-root": { py: 0.5 },
      "& .MuiMenuItem-root": {
        fontFamily: FONT,
        fontSize: 14.5,
        fontWeight: 500,
        color: C.ink,
        mx: 0.5,
        my: 0.25,
        px: 1.5,
        py: 1,
        borderRadius: "8px",
        "&:hover": { bgcolor: "#F2F4F7" },
        "&.Mui-selected": { bgcolor: "#E8F0FA", color: C.brand, fontWeight: 600 },
        "&.Mui-selected:hover": { bgcolor: "#DCE8F6" },
      },
    },
  },
};

const primaryBtnSx = {
  py: 1.25,
  borderRadius: "10px",
  textTransform: "none",
  fontFamily: FONT,
  fontWeight: 600,
  fontSize: 15,
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.08)",
  background: `linear-gradient(135deg, ${C.brand} 0%, ${C.brandDark} 100%)`,
  "&:hover": {
    background: `linear-gradient(135deg, ${C.brandDark} 0%, #0F355E 100%)`,
    boxShadow: "0 6px 16px rgba(34, 97, 162, 0.28)",
  },
  "&.Mui-disabled": {
    background: "#EAECF0",
    color: "#667085",
  },
};

/** Label sits above the input so it stays readable (floating labels shrank to ~10px). */
const Field = ({ label, required, full, children }) => (
  <Box sx={{ gridColumn: full ? { sm: "1 / -1" } : undefined, minWidth: 0 }}>
    <Typography
      component="label"
      sx={{
        display: "block",
        mb: 0.5,
        fontFamily: FONT,
        fontSize: 13.5,
        fontWeight: 600,
        color: C.body,
      }}
    >
      {label}
      {required ? <Box component="span" sx={{ color: C.error, ml: 0.25 }}>*</Box> : null}
    </Typography>
    {children}
  </Box>
);

const StepPill = ({ active }) => (
  <Box sx={{ display: "flex", gap: 0.75 }}>
    {[0, 1].map((i) => (
      <Box
        key={i}
        sx={{
          width: i <= active ? 28 : 16,
          height: 6,
          borderRadius: 3,
          bgcolor: i <= active ? C.brand : "#D0D5DD",
          transition: "all .2s",
        }}
      />
    ))}
  </Box>
);

const Header = ({ icon, iconBg, iconColor, step, title, subtitle, onClose }) => (
  <Box
    sx={{
      px: { xs: 2.5, sm: 4 },
      pt: { xs: 2.25, sm: 2.5 },
      pb: { xs: 1.75, sm: 2 },
      background: `linear-gradient(180deg, ${C.soft} 0%, #FFFFFF 100%)`,
      borderBottom: "1px solid #EAECF0",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          bgcolor: iconBg,
          color: iconColor,
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontWeight: 700, fontSize: { xs: 18, sm: 20 }, color: C.ink, lineHeight: 1.3 }}
      >
        {title}
      </Typography>
      {onClose ? (
        <IconButton onClick={onClose} aria-label="Close" size="small" sx={{ color: C.muted, flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      ) : (
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600, color: C.muted, mb: 0.5 }}>
            Step {step + 1} of 2
          </Typography>
          <StepPill active={step} />
        </Box>
      )}
    </Box>
    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: C.muted, mt: 1, lineHeight: 1.55 }}>
      {subtitle}
    </Typography>
  </Box>
);

/**
 * The one bank-account popup for members.
 *
 * Without `onClose` (OnboardingCompleteGuard): blocking popup shown after
 * onboarding until the company has a bank account on file — sellers are paid
 * into it. Step 2 shows Brand World's account so the member can add it as a
 * payee for paying orders. Cannot be dismissed; the only other way out is
 * logging out.
 *
 * With `onClose` (Profile page): closable add / change form, prefilled from the
 * account on file; saving closes it straight away.
 */
const BankDetailsRequiredDialog = ({ company, onSaved, onClose }) => {
  const dismissible = typeof onClose === "function";
  const existing = company?.bankdetails || {};
  const isChange = dismissible && Boolean(existing.bankAccountNo);
  const [step, setStep] = useState("form"); // form | beneficiary
  const [form, setForm] = useState({
    ...EMPTY,
    accountHolderName: existing.accountHolderName || company?.companyName || "",
    // The account number is always re-typed (and confirmed) when changing it.
    ifsc: isChange ? existing.ifsc || "" : "",
    bankName: isChange ? existing.bankName || "" : "",
    branchName: isChange ? existing.branchName || "" : "",
    accountType: (isChange && existing.accountType) || EMPTY.accountType,
  });
  const [cheque, setCheque] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: k === "ifsc" ? e.target.value.toUpperCase().trim() : e.target.value,
    }));

  const accountMismatch =
    form.confirmAccountNo && form.confirmAccountNo !== form.bankAccountNo;
  const valid =
    form.accountHolderName.trim() &&
    ACCOUNT_PATTERN.test(form.bankAccountNo) &&
    form.confirmAccountNo === form.bankAccountNo &&
    IFSC_PATTERN.test(form.ifsc) &&
    form.bankName.trim() &&
    form.branchName.trim() &&
    cheque;

  const save = async () => {
    setError("");
    if (!valid) {
      setError("Please fill every field correctly and attach a cancelled cheque.");
      return;
    }
    setSaving(true);
    const data = new FormData();
    data.append("accountHolderName", form.accountHolderName.trim());
    data.append("bankAccountNo", form.bankAccountNo);
    data.append("ifsc", form.ifsc);
    data.append("bankName", form.bankName.trim());
    data.append("branchName", form.branchName.trim());
    data.append("accountType", form.accountType);
    data.append("chequeImage", cheque);
    try {
      const res = await apiClient.put("company/update-company-bank-details", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res?.data?.success === true) {
        if (dismissible) {
          toast.success(res?.data?.message || "Bank details saved");
          onSaved?.();
        } else {
          setStep("beneficiary");
        }
      } else {
        setError(res?.data?.message || "Could not save bank details. Please try again.");
      }
    } catch (err) {
      const body = err?.response?.data;
      setError(
        (typeof body === "string" && body) ||
          body?.message ||
          "Could not save bank details. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try {
      await axios.get("/auth/logout", { withCredentials: true });
    } catch (e) {
      // Clear the session locally even if the server call fails.
    }
    clearAuthToken(axios, v1Backend, apiClient);
    localStorage.clear();
    navigateToDashboard("/login");
  };

  const accountInvalid = Boolean(form.bankAccountNo) && !ACCOUNT_PATTERN.test(form.bankAccountNo);
  const ifscInvalid = Boolean(form.ifsc) && !IFSC_PATTERN.test(form.ifsc);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={!dismissible}
      onClose={dismissible ? () => !saving && onClose() : undefined}
      PaperProps={{
        sx: {
          borderRadius: { xs: "16px", sm: "20px" },
          overflow: "hidden",
          m: { xs: 2, sm: 4 },
          width: { xs: "calc(100% - 32px)", sm: "100%" },
          boxShadow: "0 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
      sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(16, 24, 40, 0.55)", backdropFilter: "blur(4px)" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {step === "form" ? (
          <>
            <Header
              step={0}
              icon={<AccountBalanceIcon />}
              iconBg="#E8F0FA"
              iconColor={C.brand}
              title={isChange ? "Change bank account" : "Add your bank account"}
              subtitle={
                isChange
                  ? "Money for everything you sell on BXI is paid into this account. The new account must be in your company's name and is verified again before payouts use it."
                  : dismissible
                    ? "Money for everything you sell on BXI is paid into this account. It must be in your company's name."
                    : "Money for everything you sell on BXI is paid into this account. Add it to continue — it must be in your company's name."
              }
              onClose={dismissible ? () => !saving && onClose() : undefined}
            />

            <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: 2.25, pb: { xs: 2, sm: 2.5 } }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  columnGap: 2,
                  rowGap: 1.5,
                }}
              >
                <Field label="Account holder name" required full>
                  <TextField
                    fullWidth
                    value={form.accountHolderName}
                    onChange={set("accountHolderName")}
                    placeholder="As printed on your cheque"
                    sx={inputSx}
                  />
                </Field>
                <Field label="Account number" required>
                  <TextField
                    fullWidth
                    value={form.bankAccountNo}
                    onChange={(e) => setForm((f) => ({ ...f, bankAccountNo: e.target.value.replace(/\D/g, "") }))}
                    placeholder="Enter account number"
                    inputProps={{ inputMode: "numeric", maxLength: 20 }}
                    error={accountInvalid}
                    helperText={accountInvalid ? "6–20 digits" : undefined}
                    sx={inputSx}
                  />
                </Field>
                <Field label="Confirm account number" required>
                  <TextField
                    fullWidth
                    value={form.confirmAccountNo}
                    onChange={(e) => setForm((f) => ({ ...f, confirmAccountNo: e.target.value.replace(/\D/g, "") }))}
                    placeholder="Re-enter account number"
                    inputProps={{ inputMode: "numeric", maxLength: 20 }}
                    error={Boolean(accountMismatch)}
                    helperText={accountMismatch ? "Account numbers don't match" : undefined}
                    onPaste={(e) => e.preventDefault()}
                    sx={inputSx}
                  />
                </Field>
                <Field label="IFSC code" required>
                  <TextField
                    fullWidth
                    value={form.ifsc}
                    onChange={set("ifsc")}
                    placeholder="e.g. HDFC0001234"
                    inputProps={{ maxLength: 11, style: { letterSpacing: "0.04em" } }}
                    error={ifscInvalid}
                    helperText={ifscInvalid ? "11 characters, e.g. HDFC0001234" : undefined}
                    sx={inputSx}
                  />
                </Field>
                <Field label="Account type">
                  <TextField
                    select
                    fullWidth
                    value={form.accountType}
                    onChange={set("accountType")}
                    SelectProps={{ MenuProps: menuProps }}
                    sx={inputSx}
                  >
                    <MenuItem value="Current">Current</MenuItem>
                    <MenuItem value="Savings">Savings</MenuItem>
                    <MenuItem value="Cash Credit">Cash Credit</MenuItem>
                    <MenuItem value="Overdraft">Overdraft</MenuItem>
                  </TextField>
                </Field>
                <Field label="Bank name" required>
                  <TextField
                    fullWidth
                    value={form.bankName}
                    onChange={set("bankName")}
                    placeholder="e.g. HDFC Bank"
                    sx={inputSx}
                  />
                </Field>
                <Field label="Branch" required>
                  <TextField
                    fullWidth
                    value={form.branchName}
                    onChange={set("branchName")}
                    placeholder="e.g. Andheri East"
                    sx={inputSx}
                  />
                </Field>
              </Box>

              <Box sx={{ mt: 1.5 }}>
                <Field label="Cancelled cheque or bank statement" required>
                  {cheque ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1.25,
                        px: 1.5,
                        border: `1.5px solid ${C.brand}`,
                        bgcolor: "#F5F9FF",
                        borderRadius: "12px",
                      }}
                    >
                      <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "#E8F0FA", color: C.brand, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <InsertDriveFileOutlinedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.ink }}>
                          {cheque.name}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>
                          {(cheque.size / 1024 / 1024).toFixed(2)} MB · Ready to upload
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => setCheque(null)} aria-label="Remove file" sx={{ color: C.muted }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      component="label"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1.25,
                        px: 1.5,
                        border: "1.5px dashed #B9C4D3",
                        borderRadius: "12px",
                        bgcolor: "#FAFBFD",
                        cursor: "pointer",
                        transition: "all .15s",
                        "&:hover": { borderColor: C.brand, bgcolor: "#F5F9FF" },
                      }}
                    >
                      <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "#E8F0FA", color: C.brand, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <CloudUploadOutlinedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.4 }}>
                          <Box component="span" sx={{ color: C.brand }}>Click to upload</Box> your document
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.muted, lineHeight: 1.4 }}>
                          JPG, PNG or PDF
                        </Typography>
                      </Box>
                      <input
                        hidden
                        type="file"
                        accept="image/png,image/jpeg,application/pdf"
                        onChange={(e) => setCheque(e.target.files?.[0] || null)}
                      />
                    </Box>
                  )}
                </Field>
              </Box>

              {error ? (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: "10px", fontFamily: FONT, fontSize: 13.5 }}>
                  {error}
                </Alert>
              ) : null}

              <Button
                variant="contained"
                fullWidth
                disableElevation
                onClick={save}
                disabled={saving || !valid}
                sx={{ ...primaryBtnSx, mt: 2.25 }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : "Save bank details"}
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mt: 1.25 }}>
                <LockOutlinedIcon sx={{ fontSize: 15, color: C.muted }} />
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.muted }}>
                  Used only to pay out your sales on BXI.
                </Typography>
              </Box>

              <Box sx={{ textAlign: "center", mt: 0.25 }}>
                <Button
                  onClick={dismissible ? () => !saving && onClose() : logout}
                  sx={{ textTransform: "none", fontFamily: FONT, fontSize: 13.5, fontWeight: 500, color: C.muted, "&:hover": { color: C.ink, bgcolor: "transparent" } }}
                >
                  {dismissible ? "Cancel" : "Not now? Log out"}
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Header
              step={1}
              icon={<CheckCircleIcon />}
              iconBg="#ECFDF3"
              iconColor={C.success}
              title="Bank account saved"
              subtitle="One more thing: add Brand World as a beneficiary in your bank now, so paying for your orders later takes a minute. You can also find these details any time on your Profile page."
            />
            <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: 2.25, pb: { xs: 2, sm: 2.5 } }}>
              <BrandWorldBankDetails showUpi={false} />
              <Button
                variant="contained"
                fullWidth
                disableElevation
                onClick={() => onSaved?.()}
                sx={{ ...primaryBtnSx, mt: 2.25 }}
              >
                Continue to dashboard
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BankDetailsRequiredDialog;
